import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import videoDownloadServiceInstance from '../../services/VideoDownloadService';
import { formatVideoName } from '../../utils/formatUtils';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const BulkDownloadDialog = ({ 
    open, 
    onClose, 
    videoFiles = [], 
    folderName = 'folder',
    currentPath = '/'
}) => {
    useTrackUsage('BulkDownloadDialog', 'dialog', 'folder-browser', open);
    const [selectedVideos, setSelectedVideos] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadOptions, setDownloadOptions] = useState({
        originalVideo: true,
        compressedVideo: true,
        srtFiles: true,
    });
    const [selectedLanguages, setSelectedLanguages] = useState(new Set());

    const extractVideoLanguages = (video) => {
        const langs = Array.isArray(video?.videoLanguages) ? video.videoLanguages : [];
        return langs
            .map((lang) => {
                if (typeof lang === 'string') return lang;
                const languageName = String(lang?.languageName || '').trim();
                const hasVtt = Boolean(lang?.vttFileUrl);
                const status = String(lang?.status || '').toUpperCase();
                const isReady = !status || status === 'READY';
                if (languageName && hasVtt && isReady) return languageName;
                return '';
            })
            .map((name) => String(name || '').trim())
            .filter(Boolean);
    };

    const collectAvailableLanguages = (videos) => {
        const names = new Set();
        videos.forEach((video) => {
            extractVideoLanguages(video).forEach((name) => names.add(name));
        });
        return Array.from(names).sort((a, b) => a.localeCompare(b));
    };

    const getSelectedVideos = () => {
        return videoFiles.filter((video, index) => {
            const videoId = video.data || video._id || index;
            return selectedVideos.has(videoId);
        });
    };

    const availableLanguages = React.useMemo(() => {
        const selected = getSelectedVideos();
        const sourceVideos = selected.length > 0 ? selected : videoFiles;
        return collectAvailableLanguages(sourceVideos);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVideos, videoFiles]);

    const sourceVideos = React.useMemo(() => {
        const selected = getSelectedVideos();
        return selected.length > 0 ? selected : videoFiles;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVideos, videoFiles]);

    const languageVideoCoverage = React.useMemo(() => {
        const counts = {};
        sourceVideos.forEach((video) => {
            const uniqueVideoLanguages = new Set(extractVideoLanguages(video));
            uniqueVideoLanguages.forEach((lang) => {
                counts[lang] = (counts[lang] || 0) + 1;
            });
        });
        return counts;
    }, [sourceVideos]);

    const originalAvailableCount = React.useMemo(
        () => sourceVideos.filter((video) => Boolean(video?.videoUrl)).length,
        [sourceVideos],
    );

    const compressedAvailableCount = React.useMemo(
        () => sourceVideos.filter((video) => Boolean(video?.compressedUrl)).length,
        [sourceVideos],
    );

    const srtAvailableCount = React.useMemo(
        () => sourceVideos.filter((video) => extractVideoLanguages(video).length > 0).length,
        [sourceVideos],
    );

    React.useEffect(() => {
        if (open) {
            // Reset selection when dialog opens
            setSelectedVideos(new Set());
            setSelectAll(false);
            setDownloadOptions({
                originalVideo: true,
                compressedVideo: true,
                srtFiles: true,
            });
            setSelectedLanguages(new Set(collectAvailableLanguages(videoFiles)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, videoFiles]);

    React.useEffect(() => {
        if (!open) return;
        // Keep selected languages valid as selected videos change.
        setSelectedLanguages((prev) => {
            const valid = new Set(availableLanguages);
            const filtered = new Set(Array.from(prev).filter((lang) => valid.has(lang)));
            if (filtered.size > 0) return filtered;
            return new Set(availableLanguages);
        });
    }, [availableLanguages, open]);

    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        if (checked) {
            const allVideoIds = videoFiles.map((video, index) => video.data || video._id || index);
            setSelectedVideos(new Set(allVideoIds));
        } else {
            setSelectedVideos(new Set());
        }
    };

    const handleVideoSelect = (video, index) => {
        const videoId = video.data || video._id || index;
        const newSelected = new Set(selectedVideos);
        
        if (newSelected.has(videoId)) {
            newSelected.delete(videoId);
        } else {
            newSelected.add(videoId);
        }
        
        setSelectedVideos(newSelected);
        setSelectAll(newSelected.size === videoFiles.length);
    };

    const handleDownload = async () => {
        const videosToDownload = getSelectedVideos();
        
        if (videosToDownload.length === 0) {
            return;
        }

        setDownloading(true);
        
        try {
            // Close this dialog before starting download to prevent focus conflicts
            onClose();
            
            // Small delay to ensure dialog is fully closed
            await new Promise(resolve => setTimeout(resolve, 200));
            
            await videoDownloadServiceInstance.downloadFolderVideos(
                videosToDownload, 
                folderName,
                {
                    includeOriginalVideo: downloadOptions.originalVideo,
                    includeCompressedVideo: downloadOptions.compressedVideo,
                    includeSrtFiles: downloadOptions.srtFiles,
                    selectedLanguages: Array.from(selectedLanguages),
                }
            );
        } catch (error) {
            console.error('Bulk download failed:', error);
            // Reopen dialog if download fails and dialog was closed
            if (!open) {
                // This would need to be handled by parent component
            }
        } finally {
            setDownloading(false);
        }
    };

    const selectedCount = selectedVideos.size;
    const sourceVideoCount = sourceVideos.length;
    const hasAtLeastOneDownloadType =
        downloadOptions.originalVideo || downloadOptions.compressedVideo || downloadOptions.srtFiles;
    const srtSelectionInvalid = downloadOptions.srtFiles && selectedLanguages.size === 0;

    const estimatedFiles = React.useMemo(() => {
        const selectedLangsLower = new Set(Array.from(selectedLanguages).map((lang) => String(lang).toLowerCase()));
        return sourceVideos.reduce((sum, video) => {
            let count = 0;
            if (downloadOptions.originalVideo && video?.videoUrl) count += 1;
            if (downloadOptions.compressedVideo && video?.compressedUrl) count += 1;
            if (downloadOptions.srtFiles) {
                const matched = extractVideoLanguages(video).filter((lang) => selectedLangsLower.has(lang.toLowerCase()));
                count += matched.length;
            }
            return sum + count;
        }, 0);
    }, [sourceVideos, selectedLanguages, downloadOptions]);

    return (
        <Dialog 
            open={open} 
            onClose={!downloading ? onClose : undefined}
            maxWidth="md" 
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderIcon color="primary" />
                    <Box>
                        <Typography variant="h6">
                            Download from Folder
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {currentPath}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent>
                {videoFiles.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                            No video files found in this folder
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectAll}
                                        indeterminate={selectedCount > 0 && selectedCount < videoFiles.length}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        icon={<CheckBoxOutlineBlankIcon />}
                                        checkedIcon={<CheckBoxIcon />}
                                    />
                                }
                                label={`Select All (${videoFiles.length} videos)`}
                            />
                            
                            {selectedCount > 0 && (
                                <Chip 
                                    label={`${selectedCount} selected`}
                                    color="primary"
                                    size="small"
                                />
                            )}
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                What to download
                            </Typography>

                            <Box
                                sx={{
                                    mb: 1.5,
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'background.default',
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Download Summary
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Scope: {sourceVideoCount} video{sourceVideoCount !== 1 ? 's' : ''}
                                    {selectedCount > 0 ? ' (selected)' : ' (all in folder)'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Estimated files: {estimatedFiles}
                                </Typography>
                            </Box>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={downloadOptions.originalVideo}
                                        disabled={originalAvailableCount === 0}
                                        onChange={(e) =>
                                            setDownloadOptions((prev) => ({ ...prev, originalVideo: e.target.checked }))
                                        }
                                    />
                                }
                                label={`Original Video (${originalAvailableCount}/${sourceVideoCount} videos)`}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={downloadOptions.compressedVideo}
                                        disabled={compressedAvailableCount === 0}
                                        onChange={(e) =>
                                            setDownloadOptions((prev) => ({ ...prev, compressedVideo: e.target.checked }))
                                        }
                                    />
                                }
                                label={`Compressed Video (${compressedAvailableCount}/${sourceVideoCount} videos)`}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={downloadOptions.srtFiles}
                                        disabled={srtAvailableCount === 0}
                                        onChange={(e) =>
                                            setDownloadOptions((prev) => ({ ...prev, srtFiles: e.target.checked }))
                                        }
                                    />
                                }
                                label={`SRT files (${srtAvailableCount}/${sourceVideoCount} videos)`}
                            />

                            {downloadOptions.srtFiles && (
                                <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        Select subtitle languages (multiple)
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {availableLanguages.length > 0 ? (
                                            availableLanguages.map((lang) => {
                                                const selected = selectedLanguages.has(lang);
                                                return (
                                                    <Chip
                                                        key={lang}
                                                        label={`${lang} (${languageVideoCoverage[lang] || 0})`}
                                                        clickable
                                                        color={selected ? 'primary' : 'default'}
                                                        variant={selected ? 'filled' : 'outlined'}
                                                        onClick={() => {
                                                            setSelectedLanguages((prev) => {
                                                                const next = new Set(prev);
                                                                if (next.has(lang)) next.delete(lang);
                                                                else next.add(lang);
                                                                return next;
                                                            });
                                                        }}
                                                    />
                                                );
                                            })
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No subtitle languages found in the selected videos.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {!hasAtLeastOneDownloadType && (
                                <Alert severity="warning" sx={{ mt: 1.5 }}>
                                    Select at least one download type.
                                </Alert>
                            )}
                            {srtSelectionInvalid && (
                                <Alert severity="warning" sx={{ mt: 1.5 }}>
                                    Select at least one subtitle language for SRT download.
                                </Alert>
                            )}
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {videoFiles.map((video, index) => {
                                const videoId = video.data || video._id || index;
                                const isSelected = selectedVideos.has(videoId);
                                
                                return (
                                    <ListItem key={videoId} disablePadding>
                                        <ListItemButton 
                                            onClick={() => handleVideoSelect(video, index)}
                                            disabled={downloading}
                                        >
                                            <ListItemIcon>
                                                <Checkbox
                                                    checked={isSelected}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    icon={<CheckBoxOutlineBlankIcon />}
                                                    checkedIcon={<CheckBoxIcon />}
                                                />
                                            </ListItemIcon>
                                            <ListItemIcon>
                                                <VideoFileIcon color="secondary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={formatVideoName(video.videoName || video.name)}
                                                secondary={
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                        {video.duration && (
                                                            <Chip 
                                                                label={video.duration} 
                                                                size="small" 
                                                                variant="outlined" 
                                                            />
                                                        )}
                                                        {video.videoLanguages && (
                                                            <Chip 
                                                                label={`${video.videoLanguages.length} languages`}
                                                                size="small" 
                                                                variant="filled"
                                                                sx={{
                                                                    backgroundColor: 'primary.main',
                                                                    color: 'primary.contrastText',
                                                                    fontWeight: 'medium',
                                                                    '&:hover': {
                                                                        backgroundColor: 'primary.dark',
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondaryTypographyProps={{
                                                    component: 'div'
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                    onClick={onClose} 
                    disabled={downloading}
                >
                    Cancel
                </Button>
                
                <Button
                    variant="contained"
                    startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                    onClick={handleDownload}
                    disabled={selectedCount === 0 || downloading || !hasAtLeastOneDownloadType || srtSelectionInvalid}
                >
                    {downloading 
                        ? 'Downloading...' 
                        : `Download ${selectedCount} Video${selectedCount !== 1 ? 's' : ''}`
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkDownloadDialog;